import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, ChevronRight, ChevronLeft, Calendar, User, Tag, ArrowLeft, Loader,
  Share2, Clock, Instagram, Facebook, ExternalLink, Newspaper, X, ZoomIn, Download, Building2
} from 'lucide-react';
import { fetchBeritaBySlug, fetchPengumumanUnitBySlug, fetchBeritaRelated, fetchBerita, getImageUrl, formatTanggalIndonesia } from '../utils/api';
import SEO from '../components/SEO';
import GalleryFotoKegiatan from '../components/GalleryFotoKegiatan';

/* ───────── helpers ───────── */
const capKategori = (k) => k ? k.charAt(0).toUpperCase() + k.slice(1) : '';

const parseTags = (tags) => {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags;
  if (typeof tags === 'string') {
    try { const p = JSON.parse(tags); return Array.isArray(p) ? p : []; }
    catch { return []; }
  }
  return [];
};

const parseTombolLink = (str) => {
  if (!str || typeof str !== 'string') return [];
  const links = str.split(',').map(s => s.trim()).filter(Boolean);
  return links.map((item, index) => {
    let label = `Kunjungi Link ${index + 1}`;
    let url = item;
    if (item.includes('|')) {
      const parts = item.split('|');
      label = parts[0].trim();
      url = parts[1].trim();
    }
    return { label, url };
  }).slice(0, 3);
};

/* ───────── Action Buttons ───────── */
const ActionButtons = ({ linkString }) => {
  const links = parseTombolLink(linkString);
  if (links.length === 0) return null;

  return (
    <div className="mt-8 mb-4 flex flex-col sm:flex-row flex-wrap gap-3">
      {links.map((link, idx) => (
        <a
          key={idx}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold transition-all duration-300
            ${idx === 0 
              ? 'bg-gradient-to-r from-[#7B2FBE] to-[#9333EA] text-white shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 hover:-translate-y-0.5' 
              : 'bg-purple-50 dark:bg-[#201E38] text-[#7B2FBE] dark:text-purple-300 hover:bg-purple-100 dark:hover:bg-[#23233D] border border-purple-100 dark:border-gray-800 hover:-translate-y-0.5 shadow-sm'}`}
        >
          {link.label}
          <ExternalLink size={14} className={idx === 0 ? "text-white/80" : "text-[#7B2FBE]/80 dark:text-purple-300/80"} />
        </a>
      ))}
    </div>
  );
};

const TikTokIcon = ({ size = 16 }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width={size} height={size}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.32 6.32 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.19a8.18 8.18 0 0 0 4.78 1.52V6.27a4.85 4.85 0 0 1-1.01-.42z" />
  </svg>
);

/* ───────── Social embeds ───────── */

/**
 * Instagram — pakai iframe embed resmi.
 * blockquote hanya untuk post tunggal; untuk profil kita gunakan
 * URL embed https://www.instagram.com/{user}/embed/
 */
const InstagramEmbed = () => (
  <div className="sidebar-card">
    <SidebarHeading
      icon={<Instagram size={15} className="text-pink-500" />}
      label="Instagram"
      href="https://www.instagram.com/stikesharapanibu/"
    />
    <div className="overflow-hidden rounded-xl" style={{ height: 460 }}>
      <iframe
        src="https://www.instagram.com/stikesharapanibu/embed/"
        title="Instagram STIKES HI"
        width="100%"
        height="460"
        frameBorder="0"
        scrolling="no"
        allowTransparency="true"
        allow="encrypted-media"
        style={{ border: 'none', borderRadius: '12px', display: 'block' }}
      />
    </div>
    <a
      href="https://www.instagram.com/stikesharapanibu/"
      target="_blank"
      rel="noopener noreferrer"
      className="social-follow-btn"
      style={{ background: 'linear-gradient(135deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)' }}
    >
      <Instagram size={14} /> Follow di Instagram
    </a>
  </div>
);

/**
 * TikTok — tidak ada official profile embed, pakai desain card premium.
 * TikTok hanya mendukung embed per-video, bukan halaman profil.
 */
const TikTokEmbed = () => (
  <div className="sidebar-card">
    <SidebarHeading
      icon={<TikTokIcon size={15} />}
      label="TikTok"
      href="https://www.tiktok.com/@stikeshijambi"
    />
    <div
      className="rounded-xl overflow-hidden"
      style={{ background: 'linear-gradient(160deg,#010101 0%,#1a1a1a 60%,#010101 100%)' }}
    >
      {/* Decorative top strip */}
      <div style={{ height: 4, background: 'linear-gradient(90deg,#69C9D0,#EE1D52)' }} />

      <div className="flex flex-col items-center gap-4 py-8 px-4 text-center">
        {/* Profile Image with official logo */}
        <div className="relative">
          <div
            className="w-20 h-20 rounded-full bg-white flex items-center justify-center overflow-hidden border-2 border-white/20 shadow-xl"
          >
            <img 
              src="https://storage.stikes-hi.ac.id/stikes-hi-media/3fbebd76-22d3-4641-a12f-ec3c51280150.png" 
              alt="STIKES HI Logo" 
              className="w-14 h-14 object-contain"
            />
          </div>
          <span
            className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center border-2 border-[#010101]"
            style={{ background: '#EE1D52' }}
          >
            <TikTokIcon size={14} />
          </span>
        </div>

        <div>
          <p className="text-white font-bold text-base font-heading">@stikeshijambi</p>
          <p className="text-white/60 text-xs mt-0.5">STIKES Harapan Ibu Jambi</p>
        </div>

        {/* Fake stats row */}
        <div className="grid grid-cols-3 gap-2 w-full">
          {[['Video', '100+'], ['Pengikut', '2K+'], ['Like', '10K+']].map(([k, v]) => (
            <div key={k} className="bg-white/5 rounded-lg py-2">
              <p className="text-white font-bold text-sm">{v}</p>
              <p className="text-white/50 text-[10px]">{k}</p>
            </div>
          ))}
        </div>

        <p className="text-white/60 text-[11px] leading-relaxed">
          Konten edukatif, kegiatan kampus &amp; kehidupan mahasiswa STIKES HI Jambi.
        </p>
      </div>
    </div>
    <a
      href="https://www.tiktok.com/@stikeshijambi"
      target="_blank"
      rel="noopener noreferrer"
      className="social-follow-btn"
      style={{ background: '#010101', border: '1px solid #333' }}
    >
      <TikTokIcon size={14} /> Follow di TikTok
    </a>
  </div>
);

/**
 * Facebook Page Plugin — iframe-based (tidak butuh SDK, tidak ada Script error)
 */
const FacebookEmbed = () => {
  const fbSrc = `https://www.facebook.com/plugins/page.php?href=${encodeURIComponent('https://www.facebook.com/stikeshiofficial')}&tabs=timeline&width=340&height=350&small_header=true&adapt_container_width=true&hide_cover=false&show_facepile=true&appId`;

  return (
    <div className="sidebar-card">
      <SidebarHeading
        icon={<Facebook size={15} className="text-blue-500" />}
        label="Facebook"
        href="https://www.facebook.com/stikeshiofficial"
      />
      <div className="overflow-hidden rounded-xl bg-white dark:bg-[#201E38]">
        <iframe
          src={fbSrc}
          title="Facebook STIKES HI"
          width="100%"
          height="350"
          style={{ border: 'none', overflow: 'hidden', display: 'block' }}
          scrolling="no"
          frameBorder="0"
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          allowFullScreen
        />
      </div>
      <a
        href="https://www.facebook.com/stikeshiofficial"
        target="_blank"
        rel="noopener noreferrer"
        className="social-follow-btn"
        style={{ background: '#1877F2' }}
      >
        <Facebook size={14} /> Like di Facebook
      </a>
    </div>
  );
};

/* ───────── Reusable sidebar heading ───────── */
const SidebarHeading = ({ icon, label, href }) => (
  <div className="flex items-center justify-between mb-3">
    <div className="flex items-center gap-2">
      {icon}
      <h3 className="font-bold text-[#1A1A2E] dark:text-white text-sm uppercase tracking-wide font-heading">
        {label}
      </h3>
    </div>
    {href && (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-gray-400 hover:text-[#7B2FBE] transition-colors"
      >
        <ExternalLink size={13} />
      </a>
    )}
  </div>
);

/* ───────── Related article card ───────── */
const RelatedCard = ({ article }) => {
  const thumb = getImageUrl(article.thumbnail, { width: 180, height: 120 });
  return (
    <Link
      to={`/berita/${article.slug}`}
      data-testid={`related-${article.id}`}
      className="group flex gap-3 items-start p-3 rounded-xl bg-white dark:bg-[#1A182E]
        border border-gray-100 dark:border-gray-800
        hover:border-[#7B2FBE] hover:shadow-lg hover:shadow-purple-500/10
        transition-all duration-300"
    >
      {/* Thumbnail */}
      <div className="w-20 h-16 rounded-lg overflow-hidden flex-shrink-0 relative">
        {thumb ? (
          <img
            src={thumb}
            alt={article.judul}
            loading="lazy"
            style={{ objectPosition: article.thumbnailPosition || '50% 50%' }}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#7B2FBE] to-[#1A1A2E] flex items-center justify-center">
            <Newspaper size={18} className="text-white/40" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
      </div>

      {/* Text */}
      <div className="flex-1 min-w-0">
        <span className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-[#7B2FBE] dark:text-purple-300 mb-1">
          {capKategori(article.kategori)}
        </span>
        <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-200 leading-snug group-hover:text-[#7B2FBE] transition-colors line-clamp-2">
          {article.judul}
        </h4>
        <span className="flex items-center gap-1 text-[10px] text-gray-400 dark:text-gray-500 mt-1">
          <Clock size={10} /> {formatTanggalIndonesia(article.tanggal_publikasi)}
        </span>
      </div>
    </Link>
  );
};

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
const BeritaDetail = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [latest, setLatest] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null); // null = closed
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lbDirection, setLbDirection] = useState(1);
  const lightboxImagesRef = useRef([]);
  const touchStartX = useRef(0);
  const contentRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setNotFound(false);
      setArticle(null);
      setRelated([]);

      // Slug bisa milik berita CMS atau pengumuman unit; keduanya dicoba.
      const data = (await fetchBeritaBySlug(slug))
        || (await fetchPengumumanUnitBySlug(slug).catch(() => null));
      if (!data) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setArticle(data);

      // Galeri foto kegiatan kini menyatu dalam respons berita (data.galeri).
      const [rel, lat] = await Promise.all([
        fetchBeritaRelated(data.kategori, data.id, 4),
        fetchBerita(6),
      ]);
      setRelated(rel);
      setLatest(lat.filter(b => b.id !== data.id).slice(0, 3));
      // Pengumuman unit menampilkan galerinya lewat blok "Galeri" di bawah konten;
      // berita memakai komponen "Foto Kegiatan" di sini.
      setGallery(data.unitLabel ? [] : (data.galeri || []));
      setLoading(false);
    };
    load();
  }, [slug]);

  // Handle Instagram embeds in article content
  // Menggunakan pendekatan iframe untuk menghindari cross-origin Script error
  useEffect(() => {
    if (!article?.konten) return;

    // Cari semua blockquote instagram-media yang ada di konten dan konversi ke iframe
    const timer = setTimeout(() => {
      const container = document.querySelector('.prose');
      if (!container) return;

      const igBlockquotes = container.querySelectorAll('blockquote.instagram-media');
      igBlockquotes.forEach((bq) => {
        // Ambil link post dari dalam blockquote
        const link = bq.querySelector('a[href*="instagram.com"]');
        if (!link) return;

        const postUrl = link.href;
        const iframe = document.createElement('iframe');
        iframe.src = `${postUrl}embed/`;
        iframe.width = '100%';
        iframe.height = '550';
        iframe.frameBorder = '0';
        iframe.scrolling = 'no';
        iframe.allowTransparency = 'true';
        iframe.style.border = 'none';
        iframe.style.borderRadius = '12px';
        iframe.style.maxWidth = '540px';
        iframe.style.display = 'block';
        iframe.style.margin = '16px auto';

        bq.replaceWith(iframe);
      });
    }, 100);

    return () => clearTimeout(timer);
  }, [article]);

  // Handle image zoom for article content — menggunakan pure CSS classes (bukan Tailwind dinamis)
  useEffect(() => {
    if (!article?.konten) return;
    let observer;

    const attachImageZoom = () => {
      const container = contentRef.current;
      if (!container) return;

      const images = container.querySelectorAll('img');
      images.forEach((img) => {
        if (img.dataset.zoomReady === 'true') return;
        img.dataset.zoomReady = 'true';

        // Jika gambar sudah di dalam wrapper kita, skip
        if (img.parentElement?.classList.contains('prose-img-wrapper')) return;

        // Cegah link bawaan CMS
        const parentAnchor = img.closest('a');
        if (parentAnchor) {
          parentAnchor.onclick = (e) => e.preventDefault();
        }

        // Buat wrapper dengan class CSS yang sudah kita definisikan di <style>
        const wrapper = document.createElement('div');
        wrapper.className = 'prose-img-wrapper';
        img.title = 'Klik untuk memperbesar gambar';

        // Overlay gelap
        const overlay = document.createElement('div');
        overlay.className = 'prose-img-overlay';
        const icon = document.createElement('div');
        icon.className = 'prose-img-zoom-icon';
        icon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" x2="16.65" y1="21" y2="16.65"/><line x1="11" x2="11" y1="8" y2="14"/><line x1="8" x2="14" y1="11" y2="11"/></svg>`;
        overlay.appendChild(icon);

        // Sisipkan wrapper ke DOM, pindahkan img ke dalamnya
        img.parentNode.insertBefore(wrapper, img);
        wrapper.appendChild(img);
        wrapper.appendChild(overlay);

        // Klik handler pakai index dari gallery
        wrapper.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const idx = lightboxImagesRef.current.indexOf(img.src);
          setLbDirection(1);
          setLightboxIndex(idx >= 0 ? idx : 0);
        });
      });

      if (!observer) {
        observer = new MutationObserver(attachImageZoom);
        observer.observe(container, { childList: true, subtree: true });
      }
    };

    const timer = setTimeout(attachImageZoom, 300);
    return () => {
      clearTimeout(timer);
      if (observer) observer.disconnect();
    };
  }, [article?.konten]);

  // Kumpulkan semua gambar (thumbnail + konten) ke dalam satu gallery array
  useEffect(() => {
    if (!article) return;
    const timer = setTimeout(() => {
      const imgs = [];
      const thumbUrl = getImageUrl(article.thumbnail, { width: 1200, height: 600 });
      if (thumbUrl) imgs.push(thumbUrl);
      const container = contentRef.current;
      if (container) {
        container.querySelectorAll('img').forEach((img) => {
          if (img.src && !imgs.includes(img.src)) imgs.push(img.src);
        });
      }
      lightboxImagesRef.current = imgs;
      setLightboxImages(imgs);
    }, 500); // sedikit lebih lambat dari attachImageZoom (300ms)
    return () => clearTimeout(timer);
  }, [article]);

  // Keyboard navigation untuk lightbox
  useEffect(() => {
    if (lightboxIndex === null) return;
    const total = lightboxImagesRef.current.length;
    const handleKey = (e) => {
      if (e.key === 'ArrowRight') {
        setLbDirection(1);
        setLightboxIndex((i) => (i + 1 < total ? i + 1 : i));
      } else if (e.key === 'ArrowLeft') {
        setLbDirection(-1);
        setLightboxIndex((i) => (i - 1 >= 0 ? i - 1 : i));
      } else if (e.key === 'Escape') {
        setLightboxIndex(null);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: article?.judul, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loader className="animate-spin text-[#7B2FBE]" size={40} />
      </div>
    );
  }

  if (notFound) return <Navigate to="/berita" replace />;

  const thumbnailUrl = getImageUrl(article.thumbnail, { width: 1200, height: 600 });
  const tags = parseTags(article.tags);
  const excerpt = article.deskripsi || article.excerpt || article.konten?.replace(/<[^>]+>/g, '').slice(0, 155);

  /* berita lainnya = related (sama kategori) atau fallback ke latest */
  const sidebarNews = related.length > 0 ? related : latest;

  return (
    <>
      {/* Scoped styles */}
      <style>{`
        .sidebar-card {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 16px;
        }
        .dark .sidebar-card {
          background: #0F0F1A;
          border-color: #1f2937;
        }
        .social-follow-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          width: 100%;
          margin-top: 10px;
          padding: 8px 12px;
          border-radius: 10px;
          color: #fff;
          font-size: 12px;
          font-weight: 600;
          text-decoration: none;
          transition: opacity 0.2s;
        }
        .social-follow-btn:hover { opacity: 0.88; }

        /* ── Gambar konten artikel ── */
        .prose-img-wrapper {
          position: relative;
          display: block;
          margin: 24px auto;
          border-radius: 16px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0,0,0,0.12);
          cursor: zoom-in;
          line-height: 0;
        }
        .prose-img-wrapper img {
          width: 100%;
          height: auto;
          display: block;
          margin: 0 !important;
          transition: transform 0.5s ease, opacity 0.3s ease;
        }
        .prose-img-wrapper:hover img {
          transform: scale(1.04);
          opacity: 0.92;
        }
        .prose-img-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0,0,0,0);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: background 0.3s ease, opacity 0.3s ease;
          pointer-events: none;
        }
        .prose-img-wrapper:hover .prose-img-overlay {
          background: rgba(0,0,0,0.15);
          opacity: 1;
        }
        .prose-img-zoom-icon {
          background: rgba(0,0,0,0.55);
          color: #fff;
          border-radius: 50%;
          padding: 12px;
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 16px rgba(0,0,0,0.3);
        }
      `}</style>

      <div data-testid="berita-detail-page">
        <SEO
          title={article.judul}
          description={excerpt}
          image={thumbnailUrl}
          type="article"
          article={{
            publishedTime: article.tanggal_publikasi,
            section: article.kategori || 'Berita',
            author: article.penulis || article.unitLabel || undefined,
          }}
          breadcrumb={[{ name: 'Berita', path: '/berita' }, { name: article.judul }]}
          keywords={Array.isArray(article.tags) ? article.tags : parseTags(article.tags)}
        />

        {/* ── Hero ── */}
        <div className="pt-[70px] relative bg-[#0A0A0F]">
          {thumbnailUrl ? (
            <img
              src={thumbnailUrl}
              alt={article.judul}
              style={{ objectPosition: article.thumbnailPosition || '50% 50%' }}
              className="absolute inset-0 w-full h-full object-cover opacity-30"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-[#7B2FBE] to-[#1A1A2E] opacity-60" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0F] via-[#0A0A0F]/50 to-transparent" />

          <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-white/60 text-sm mb-6 flex-wrap">
                <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">
                  <Home size={13} /> Beranda
                </Link>
                <ChevronRight size={13} />
                <Link to="/berita" className="hover:text-white transition-colors">Berita</Link>
                <ChevronRight size={13} />
                <span className="text-white/80 truncate max-w-xs">
                  {article.judul.substring(0, 50)}{article.judul.length > 50 ? '...' : ''}
                </span>
              </div>

              {/* Kategori badge */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#7B2FBE]/80 text-white">
                  <Tag size={11} /> {capKategori(article.kategori)}
                </span>
                {article.unitLabel && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-white/15 text-white border border-white/20">
                    <Building2 size={11} /> Unit {article.unitLabel}
                  </span>
                )}
              </div>

              <h1 className="text-2xl md:text-4xl font-bold text-white font-heading leading-tight mb-6 max-w-3xl">
                {article.judul}
              </h1>

              {/* Meta row */}
              <div className="flex items-center gap-5 text-white/60 text-sm flex-wrap">
                <span className="flex items-center gap-1.5">
                  <Calendar size={14} /> {formatTanggalIndonesia(article.tanggal_publikasi)}
                </span>
                {article.penulis && (
                  <span className="flex items-center gap-1.5">
                    <User size={14} /> {article.penulis}
                  </span>
                )}
                <button
                  onClick={handleShare}
                  className="flex items-center gap-1.5 hover:text-white transition-colors ml-auto"
                >
                  <Share2 size={14} /> {copied ? 'Link disalin!' : 'Bagikan'}
                </button>
              </div>
            </motion.div>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="bg-[#F8F7FF] dark:bg-[#121020]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

              {/* ── Article Content (2/3) ── */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="lg:col-span-2"
              >
                {/* Excerpt */}
                {article.Excerpt && (
                  <div className="bg-purple-50 dark:bg-purple-900/20 border-l-4 border-[#7B2FBE] rounded-r-xl p-5 mb-8">
                    <p className="text-gray-700 dark:text-gray-200 text-sm leading-relaxed italic">{article.Excerpt}</p>
                  </div>
                )}

                {/* Thumbnail gambar utama */}
                {thumbnailUrl && (
                  <div 
                    className="rounded-2xl overflow-hidden mb-6 shadow-lg relative group cursor-zoom-in" 
                    onClick={() => {
                      const idx = lightboxImagesRef.current.indexOf(thumbnailUrl);
                      setLbDirection(1);
                      setLightboxIndex(idx >= 0 ? idx : 0);
                    }}
                  >
                    <img
                      src={thumbnailUrl}
                      alt={article.judul}
                      style={{ objectPosition: article.thumbnailPosition || '50% 50%' }}
                      className="w-full object-cover max-h-96 group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <div className="bg-black/50 text-white rounded-full p-3 backdrop-blur-sm">
                        <ZoomIn size={24} />
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Galeri Foto Kegiatan ── */}
                {gallery.length > 0 && (
                  <div className="mb-8">
                    <GalleryFotoKegiatan
                      photos={gallery}
                      title="Foto Kegiatan"
                      defaultMode="slider"
                    />
                  </div>
                )}

                {article.unitLabel && (
                  <div className="flex items-center gap-3 mb-6 rounded-xl bg-[#7B2FBE]/5 dark:bg-purple-400/10 border border-[#7B2FBE]/15 dark:border-purple-400/20 px-4 py-3">
                    <div className="w-9 h-9 rounded-lg bg-[#7B2FBE]/10 dark:bg-purple-400/15 text-[#7B2FBE] dark:text-purple-300 grid place-items-center flex-shrink-0">
                      <Building2 size={16} />
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                      Pengumuman resmi ini diterbitkan oleh <strong className="text-[#1A1A2E] dark:text-white">Unit {article.unitLabel}</strong> STIKES Harapan Ibu Jambi.
                    </p>
                  </div>
                )}

                {/* Konten HTML */}
                <div
                  ref={contentRef}
                  className="prose prose-sm dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: article.konten }}
                />

                {/* Galeri & lampiran pengumuman unit */}
                {article.galeri?.length > 0 && (
                  <div className="mt-8">
                    <h3 className="font-heading text-sm font-bold text-[#1A1A2E] dark:text-white mb-3">Galeri</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {article.galeri.map((image) => (
                        <a
                          key={image.id}
                          href={image.url}
                          target="_blank"
                          rel="noreferrer"
                          className="block aspect-[4/3] overflow-hidden rounded-xl border border-gray-100 dark:border-gray-800"
                        >
                          <img
                            src={`${image.url}?w=480`}
                            alt={image.caption || 'Gambar pengumuman'}
                            loading="lazy"
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {(article.lampiranUrl || article.tautan) && (
                  <div className="mt-6 flex flex-wrap gap-3">
                    {article.lampiranUrl && (
                      <a
                        href={article.lampiranUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-xl border border-[#7B2FBE] px-4 py-2 text-sm text-[#7B2FBE] transition-colors hover:bg-[#7B2FBE] hover:text-white"
                      >
                        <Download size={14} /> {article.lampiranNama || 'Unduh lampiran'}
                      </a>
                    )}
                    {article.tautan && (
                      <a
                        href={article.tautan}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 rounded-xl border border-[#7B2FBE] px-4 py-2 text-sm text-[#7B2FBE] transition-colors hover:bg-[#7B2FBE] hover:text-white"
                      >
                        <ExternalLink size={14} /> Tautan terkait
                      </a>
                    )}
                  </div>
                )}

                {/* Tombol Action (CMS) */}
                <ActionButtons linkString={article.tombol_link} />

                {/* Tags */}
                {tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <span className="text-xs text-gray-500 dark:text-gray-400 mr-1 flex items-center gap-1">
                      <Tag size={11} /> Tags:
                    </span>
                    {tags.map(tag => (
                      <span key={tag} className="px-3 py-1 rounded-full bg-purple-50 dark:bg-purple-900/30 text-[#7B2FBE] dark:text-purple-300 text-xs font-medium">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Share row */}
                <div className="mt-6 p-4 rounded-xl bg-white dark:bg-[#1A182E] border border-gray-100 dark:border-gray-800 flex items-center justify-between gap-4 flex-wrap">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Bagikan artikel ini:</span>
                  <div className="flex gap-2">
                    <a
                      href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#1877F2] text-white hover:opacity-90 transition-opacity"
                    >
                      <Facebook size={13} /> Facebook
                    </a>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(article.judul + ' ' + window.location.href)}`}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#25D366] text-white hover:opacity-90 transition-opacity"
                    >
                      WhatsApp
                    </a>
                    <button
                      onClick={handleShare}
                      className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors"
                    >
                      <Share2 size={13} /> {copied ? 'Tersalin!' : 'Salin Link'}
                    </button>
                  </div>
                </div>

                {/* Back nav */}
                <div className="mt-8 flex items-center justify-between">
                  <Link to="/berita" className="flex items-center gap-2 text-sm text-[#7B2FBE] hover:underline font-medium">
                    <ArrowLeft size={14} /> Kembali ke Berita
                  </Link>
                </div>
              </motion.div>

              {/* ── Sidebar (1/3) ── */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.15 }}
                className="lg:col-span-1"
              >
                <div className="sticky top-24 space-y-6">

                  {/* 1. Berita Terkait / Lainnya */}
                  <div className="sidebar-card">
                    <SidebarHeading
                      icon={<Newspaper size={15} className="text-[#7B2FBE]" />}
                      label={related.length > 0 ? `Berita ${capKategori(article.kategori)} Lainnya` : 'Berita Terbaru'}
                    />
                    <div className="space-y-3">
                      {sidebarNews.map(a => <RelatedCard key={a.id} article={a} />)}
                      {sidebarNews.length === 0 && (
                        <p className="text-xs text-gray-400 dark:text-gray-500">Belum ada berita lainnya.</p>
                      )}
                    </div>
                    <Link
                      to="/berita"
                      className="mt-4 flex items-center justify-center gap-1.5 text-xs font-semibold text-[#7B2FBE] hover:underline"
                    >
                      Lihat Semua Berita <ChevronRight size={13} />
                    </Link>
                  </div>

                  {/* 2. CTA Pendaftaran */}
                  <div className="sidebar-card" style={{ background: 'linear-gradient(135deg,#7B2FBE,#9333EA)', border: 'none' }}>
                    <h4 className="font-bold font-heading mb-2 text-sm text-white">Info Pendaftaran</h4>
                    <p className="text-white/80 text-xs mb-4 leading-relaxed">
                      Tertarik bergabung di STIKES HI Jambi? Daftar sekarang dan raih masa depan di bidang kesehatan.
                    </p>
                    <Link
                      to="/kontak"
                      className="inline-block w-full text-center bg-white text-[#7B2FBE] text-xs font-semibold px-4 py-2.5 rounded-xl hover:bg-purple-50 transition-colors"
                    >
                      Hubungi Kami
                    </Link>
                  </div>

                  {/* 3. Instagram embed */}
                  <InstagramEmbed />

                  {/* 4. TikTok card */}
                  <TikTokEmbed />

                  {/* 5. Facebook Page Plugin */}
                  <FacebookEmbed />

                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Lightbox Gallery ── */}
      {lightboxIndex !== null && lightboxImages.length > 0 && (() => {
        const total = lightboxImages.length;
        const isFirst = lightboxIndex === 0;
        const isLast = lightboxIndex === total - 1;
        const goPrev = (e) => { e?.stopPropagation(); if (!isFirst) { setLbDirection(-1); setLightboxIndex((i) => i - 1); } };
        const goNext = (e) => { e?.stopPropagation(); if (!isLast) { setLbDirection(1); setLightboxIndex((i) => i + 1); } };
        return (
          <div
            className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-sm flex flex-col select-none"
            onClick={() => setLightboxIndex(null)}
            onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
            onTouchEnd={(e) => {
              const diff = touchStartX.current - e.changedTouches[0].clientX;
              if (Math.abs(diff) > 50) { diff > 0 ? goNext() : goPrev(); }
            }}
          >
            {/* Top bar: counter + close */}
            <div className="flex-none flex items-center justify-between px-4 sm:px-8 pt-4 sm:pt-5 pb-2">
              <span className="text-white/70 text-xs sm:text-sm font-medium bg-white/10 rounded-full px-3 py-1 cursor-default" onClick={(e) => e.stopPropagation()}>
                {lightboxIndex + 1} / {total}
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(null); }}
                className="text-white/70 hover:text-white bg-white/10 hover:bg-white/25 rounded-full p-2 sm:p-2.5 transition-all"
                aria-label="Tutup"
              >
                <X size={20} />
              </button>
            </div>

            {/* Image area */}
            <div
              className="flex-1 flex items-center justify-center relative overflow-hidden px-10 sm:px-20"
            >
              {/* Prev */}
              <button
                onClick={goPrev}
                disabled={isFirst}
                className={`absolute left-2 sm:left-4 z-10 rounded-full p-2.5 sm:p-3 transition-all backdrop-blur-sm
                  ${isFirst ? 'opacity-20 cursor-default' : 'text-white/80 hover:text-white bg-white/10 hover:bg-white/25'}`}
                aria-label="Sebelumnya"
              >
                <ChevronLeft size={26} />
              </button>

              <AnimatePresence mode="wait" custom={lbDirection}>
                <motion.img
                  key={lightboxIndex}
                  custom={lbDirection}
                  initial={(dir) => ({ opacity: 0, x: dir * 80 })}
                  animate={{ opacity: 1, x: 0 }}
                  exit={(dir) => ({ opacity: 0, x: -dir * 80 })}
                  transition={{ duration: 0.25, ease: 'easeInOut' }}
                  src={lightboxImages[lightboxIndex]}
                  alt={`Gambar ${lightboxIndex + 1} dari ${total}`}
                  draggable={false}
                  className="max-w-full max-h-full object-contain rounded-xl shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
                />
              </AnimatePresence>

              {/* Next */}
              <button
                onClick={goNext}
                disabled={isLast}
                className={`absolute right-2 sm:right-4 z-10 rounded-full p-2.5 sm:p-3 transition-all backdrop-blur-sm
                  ${isLast ? 'opacity-20 cursor-default' : 'text-white/80 hover:text-white bg-white/10 hover:bg-white/25'}`}
                aria-label="Berikutnya"
              >
                <ChevronRight size={26} />
              </button>
            </div>

            {/* Dots navigator */}
            {total > 1 && (
              <div className="flex-none flex items-center justify-center gap-2 py-4 sm:py-5">
                {lightboxImages.map((_, i) => (
                  <button
                    key={i}
                    onClick={(e) => { e.stopPropagation(); setLbDirection(i > lightboxIndex ? 1 : -1); setLightboxIndex(i); }}
                    className="rounded-full transition-all duration-300 h-2"
                    style={{
                      width: i === lightboxIndex ? '24px' : '8px',
                      background: i === lightboxIndex ? '#fff' : 'rgba(255,255,255,0.35)',
                    }}
                    aria-label={`Gambar ${i + 1}`}
                  />
                ))}
              </div>
            )}

            {/* Hint swipe (mobile only) */}
            {total > 1 && (
              <p className="sm:hidden text-white/30 text-[10px] text-center pb-3 tracking-wide">← Swipe untuk navigasi →</p>
            )}
          </div>
        );
      })()}
    </>
  );
};

export default BeritaDetail;
